import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CopyIcon, XIcon } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { authClient } from '@/lib/auth-client'
import { formatDate } from '@/lib/format'
import { meQuery } from '@/lib/queries'

export const Route = createFileRoute('/_app/$org/settings/workspace')({
  component: WorkspaceSettingsPage,
})

function useOrganization() {
  return useQuery({
    queryKey: ['organization'],
    queryFn: async () => {
      const { data, error } = await authClient.organization.getFullOrganization()
      if (error) throw new Error(error.message ?? 'Could not load workspace')
      return data
    },
  })
}

function WorkspaceSettingsPage() {
  const { org } = Route.useParams()
  const { data: me } = useQuery(meQuery)
  const { data: organization, isPending } = useOrganization()
  const queryClient = useQueryClient()

  const canManage = me?.role === 'owner' || me?.role === 'admin'
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['organization'] })
    queryClient.invalidateQueries({ queryKey: ['me'] })
  }

  const [name, setName] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'member' | 'admin' | 'owner'>('member')

  const nameValue = name ?? organization?.name ?? ''

  const rename = useMutation({
    mutationFn: async () => {
      const { error } = await authClient.organization.update({ data: { name: nameValue.trim() } })
      if (error) throw new Error(error.message ?? 'Could not rename workspace')
    },
    onSuccess: () => {
      refresh()
      toast.success('Workspace renamed')
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const invite = useMutation({
    mutationFn: async () => {
      const { data, error } = await authClient.organization.inviteMember({ email, role })
      if (error) throw new Error(error.message ?? 'Could not send invitation')
      return data
    },
    onSuccess: () => {
      setEmail('')
      refresh()
      // No transactional email provider yet, so the link is handed over here.
      toast.success('Invitation created', { description: 'Copy the link to share it.' })
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const pendingInvitations = (organization?.invitations ?? []).filter(
    (invitation) => invitation.status === 'pending',
  )

  return (
    <>
      <PageHeader title="Workspace" description="Members and access for this workspace." />

      <div className="space-y-4">
        <section className="rounded-lg border border-border bg-card p-5">
          <form
            onSubmit={(event) => {
              event.preventDefault()
              rename.mutate()
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="workspace-name">Name</Label>
              <Input
                id="workspace-name"
                value={nameValue}
                onChange={(event) => setName(event.target.value)}
                disabled={!canManage}
                className="max-w-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="workspace-slug">Address</Label>
              {/* The slug is the first segment of every URL in the workspace
                  now, so it stops being an implementation detail. */}
              <Input
                id="workspace-slug"
                value={`/${org}`}
                readOnly
                disabled
                className="max-w-sm font-mono"
              />
            </div>
            {canManage && (
              <Button type="submit" size="sm" disabled={rename.isPending || !nameValue.trim()}>
                {rename.isPending ? 'Saving…' : 'Save'}
              </Button>
            )}
          </form>
        </section>

        <section className="overflow-hidden rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h2 className="text-[13px] font-medium">Members</h2>
            <span className="font-mono text-[12px] text-muted-foreground">
              {organization?.members.length ?? 0}
            </span>
          </div>

          {isPending ? (
            <div className="space-y-3 p-5">
              <Skeleton className="h-4 w-56" />
              <Skeleton className="h-4 w-44" />
            </div>
          ) : (
            <ul>
              {organization?.members.map((member) => (
                <li
                  key={member.id}
                  className="flex items-center gap-4 border-b border-border px-5 py-3 last:border-b-0"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px]">{member.user.name || member.user.email}</p>
                    <p className="truncate font-mono text-[12px] text-muted-foreground">
                      {member.user.email}
                    </p>
                  </div>
                  <span className="font-mono text-[12px] text-muted-foreground">{member.role}</span>
                  <span className="hidden font-mono text-[12px] text-muted-foreground sm:block">
                    {formatDate(member.createdAt)}
                  </span>
                  {canManage && member.userId !== me?.user.id && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Remove ${member.user.email}`}
                      onClick={async () => {
                        const { error } = await authClient.organization.removeMember({
                          memberIdOrEmail: member.id,
                        })
                        if (error) toast.error(error.message ?? 'Could not remove member')
                        else {
                          refresh()
                          toast.success('Member removed')
                        }
                      }}
                    >
                      <XIcon />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {canManage && (
          <section className="rounded-lg border border-border bg-card p-5">
            <h2 className="pb-1 text-[13px] font-medium">Invite a member</h2>
            <p className="pb-4 text-[13px] text-muted-foreground">
              Invitations are links for now — email delivery arrives with the Resend integration.
            </p>

            <form
              onSubmit={(event) => {
                event.preventDefault()
                invite.mutate()
              }}
              className="flex flex-col gap-2 sm:flex-row"
            >
              <Input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="teammate@company.com"
                required
                className="sm:max-w-xs"
              />
              <Select value={role} onValueChange={(value) => setRole(value as typeof role)}>
                <SelectTrigger className="sm:w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="owner">Owner</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" size="sm" disabled={invite.isPending || !email}>
                {invite.isPending ? 'Inviting…' : 'Invite'}
              </Button>
            </form>

            {pendingInvitations.length > 0 && (
              <ul className="mt-5 space-y-2 border-t border-border pt-4">
                {pendingInvitations.map((invitation) => (
                  <li key={invitation.id} className="flex items-center gap-3">
                    <span className="min-w-0 flex-1 truncate font-mono text-[12px]">
                      {invitation.email}
                    </span>
                    <span className="font-mono text-[12px] text-muted-foreground">
                      {invitation.role}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Copy invitation link"
                      onClick={async () => {
                        const link = `${window.location.origin}/invite/${invitation.id}`
                        await navigator.clipboard.writeText(link)
                        toast.success('Invitation link copied')
                      }}
                    >
                      <CopyIcon />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Cancel invitation"
                      onClick={async () => {
                        const { error } = await authClient.organization.cancelInvitation({
                          invitationId: invitation.id,
                        })
                        if (error) toast.error(error.message ?? 'Could not cancel')
                        else {
                          refresh()
                          toast.success('Invitation cancelled')
                        }
                      }}
                    >
                      <XIcon />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    </>
  )
}
